import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../components/ui/dialog";
import {
  Play, Pause, ChevronLeft, Volume2, SkipForward, SkipBack,
  Loader2, BookOpen, Clock, MapPin, ChevronRight, Headphones, Globe, Share, Link2, X,
  Volume1, Speaker, Star, Coins, ImageOff
} from "lucide-react";
import { contentApi, userApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { addBreadcrumb, captureError, captureMessage } from "../lib/sentry";
import { resolveMediaUrl } from "../lib/apiConfig";

const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];

const formatTime = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

/** Check if a chapter has per-chapter audio available */
const chapterHasAudio = (ch) => !!ch?.chapter_audio_url;

/** Check if item uses legacy single-file audio */
const isLegacyAudio = (item) => {
  if (!item) return false;
  // Legacy: has audio_url but no chapters have chapter_audio_url
  if (item.audio_url && item.audio_url.startsWith("https://")) {
    const chapters = item.chapters || [];
    return !chapters.some(ch => ch.chapter_audio_url);
  }
  return false;
};

const ReliablePlaceImage = ({ imageUrl, name, className, iconClassName = "w-6 h-6" }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [imageUrl]);

  if (imageUrl && !failed) {
    return (
      <img
        src={resolveMediaUrl(imageUrl)}
        alt={name}
        className={className}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <div className={`${className} bg-[#ecebe8] dark:bg-[#302f2d] flex items-center justify-center`}>
      <ImageOff className={`${iconClassName} text-[#85827d] dark:text-[#aaa7a1]`} aria-label="No image available" />
    </div>
  );
};

const AudioGuideHero = ({ imageUrl, name }) => {
  return (
    <ReliablePlaceImage
      imageUrl={imageUrl}
      name={name}
      className="w-40 h-40 object-cover rounded-2xl mx-auto shadow-lg mb-4"
      iconClassName="w-12 h-12"
    />
  );
};

export default function AudioPlayerPage() {
  const { contentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const gainNodeRef = useRef(null);
  const audioCtxRef = useRef(null);
  const heroRef = useRef(null);
  const { user, updateCredits, refreshUser } = useAuth();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeChapter, setActiveChapter] = useState(0);
  const [related, setRelated] = useState([]);

  // Chapter-by-chapter streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [genProgress, setGenProgress] = useState(null);
  const [attachedJobId, setAttachedJobId] = useState(null);
  const [chaptersReady, setChaptersReady] = useState(0);
  const pollRef = useRef(null);
  const completedToastJobIdsRef = useRef(new Set());

  // Language & speed
  const [languages, setLanguages] = useState([]);
  const [activeLang, setActiveLang] = useState(searchParams.get("lang") || user?.default_audio_language || user?.preferred_language || "en");
  const [speed, setSpeed] = useState(1);
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareLockRef = useRef(false);
  const redeemedShareTokenRef = useRef(new Set());
  const [showSpeedSlider, setShowSpeedSlider] = useState(false);
  const [speakerMode, setSpeakerMode] = useState(true); // true = speaker, false = earpiece
  const [modeToast, setModeToast] = useState(null);
  const speakerModeRef = useRef(true);
  const [langSwitchTarget, setLangSwitchTarget] = useState(null); // language code pending confirmation
  const [langSwitching, setLangSwitching] = useState(false);
  const [langGenerating, setLangGenerating] = useState(false);
  const [langJobId, setLangJobId] = useState(null);
  const fullItemRef = useRef(null); // Stores full marker data with all translations
  const [purchasedLangs, setPurchasedLangs] = useState([]); // Languages this user has paid for
  const [userRating, setUserRating] = useState(null);
  const [ratingHover, setRatingHover] = useState(0);
  const [shareAccess, setShareAccess] = useState(
    searchParams.get("share_token") ? "checking" : null
  );
  const [sharePurchasing, setSharePurchasing] = useState(false);
  const [preparingGuide, setPreparingGuide] = useState(false);

  // Legacy audio mode
  const [legacyMode, setLegacyMode] = useState(false);

  // Per-chapter durations (actual from audio element)
  const [chapterDurations, setChapterDurations] = useState({});

  const jobId = searchParams.get("job_id");
  const incomingShareToken = searchParams.get("share_token");
  const sharedPlaybackLocked = !!incomingShareToken && shareAccess !== "already_owned";

  const clearGenerationSearchParams = useCallback(() => {
    if (!searchParams.get("job_id")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("job_id");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const showCompletionToastOnce = useCallback((completedJobId, message) => {
    if (!completedJobId) {
      toast.success(message);
      return;
    }
    const storageKey = `cv_audio_job_toast_${completedJobId}`;
    if (completedToastJobIdsRef.current.has(completedJobId) || sessionStorage.getItem(storageKey)) {
      return;
    }
    completedToastJobIdsRef.current.add(completedJobId);
    sessionStorage.setItem(storageKey, "shown");
    toast.success(message);
  }, []);

  // Load supported languages once & scroll to top on mount
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    contentApi.getLanguages().then(r => setLanguages(r.data.languages || [])).catch((error) => {
      captureError(error, { action: "load_player_languages", contentId });
    });
  }, [contentId]);

  // Reset scroll flag when contentId changes (navigating between player pages)
  const hasScrolledRef = useRef(false);
  useEffect(() => {
    hasScrolledRef.current = false;
    // Override CSS scroll-behavior: smooth to ensure instant scroll works
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    return () => { document.documentElement.style.scrollBehavior = ''; };
  }, [contentId]);

  // Scroll to top when item data first loads (after loading spinner → full layout transition)
  useEffect(() => {
    if (item && !hasScrolledRef.current) {
      hasScrolledRef.current = true;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
      });
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.documentElement.style.scrollBehavior = '';
      }, 150);
    }
  }, [item]);

  // Load languages this user has purchased for this content
  useEffect(() => {
    if (user) {
      userApi.getPurchasedLanguages(contentId)
        .then(r => setPurchasedLangs(r.data.languages || []))
        .catch((error) => {
          captureError(error, { action: "load_purchased_languages", contentId });
        });
    }
  }, [contentId, user]);

  // Load user's rating for this place
  useEffect(() => {
    if (user) {
      userApi.getRating(contentId)
        .then(r => setUserRating(r.data.rating))
        .catch((error) => {
          captureError(error, { action: "load_user_rating", contentId });
        });
    }
  }, [contentId, user]);

  const handleRate = async (stars) => {
    setUserRating(stars);
    try {
      await userApi.ratePlace(contentId, stars);
    } catch (error) {
      captureError(error, { action: "save_rating", contentId, stars });
      toast.error("Failed to save rating");
    }
  };


  // Apply playback speed
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed]);

  // Apply speaker/earpiece mode via Web Audio API GainNode (works on mobile)
  useEffect(() => {
    speakerModeRef.current = speakerMode;
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = speakerMode ? 1.0 : 0.15;
    }
  }, [speakerMode]);

  // Initialize Web Audio API gain node once the audio element mounts
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || audioCtxRef.current) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const source = ctx.createMediaElementSource(audio);
      const gain = ctx.createGain();
      gain.gain.value = speakerModeRef.current ? 1.0 : 0.15;
      source.connect(gain);
      gain.connect(ctx.destination);
      audioCtxRef.current = ctx;
      gainNodeRef.current = gain;
    } catch (e) {
      // Fallback: Web Audio API not supported
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyAudioSettings = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.playbackRate = speed;
    // Resume AudioContext if suspended (mobile requires user gesture)
    if (audioCtxRef.current?.state === "suspended") {
      audioCtxRef.current.resume();
    }
  }, [speed]);


  const toggleSpeedSlider = useCallback(() => {
    setShowSpeedSlider(prev => !prev);
  }, []);

  // Check if user has PURCHASED and audio exists for this language (free to switch)
  const hasLanguageAudio = useCallback((langCode) => {
    return purchasedLangs.includes(langCode);
  }, [purchasedLangs]);

  // Instantly switch to a language the user already owns (free)
  const switchToExistingLanguage = useCallback((langCode) => {
    if (audioRef.current) { audioRef.current.pause(); setIsPlaying(false); }
    // Changing activeLang triggers a fresh, language-scoped backend request.
    // The API deliberately never returns every translation in one response.
    setActiveLang(langCode);
    setActiveChapter(0);
    setCurrentTime(0);
    setLangGenerating(false);
  }, []);

  // Handle language switch — generate new language (costs 1 credit)
  const handleLanguageSwitch = async () => {
    if (!langSwitchTarget) return;
    setLangSwitching(true);
    addBreadcrumb("Language switch requested", "content.language", {
      contentId,
      targetLang: langSwitchTarget,
    });
    // Stop current playback
    if (audioRef.current) { audioRef.current.pause(); setIsPlaying(false); }

    try {
      const response = await contentApi.generateSummary(contentId, langSwitchTarget, true);
      const data = response.data;
      if (data.status === "complete" && data.marker) {
        // Was cached server-side (another user may have generated it)
        fullItemRef.current = data.marker;
        setPurchasedLangs(prev => [...new Set([...prev, langSwitchTarget])]);
        switchToExistingLanguage(langSwitchTarget);
        const remaining = Math.max(0, (user?.credits || 0) - 1);
        updateCredits(remaining);
        toast.success(`Switched to ${languages.find(l => l.code === langSwitchTarget)?.label || langSwitchTarget}. ${remaining} credit${remaining !== 1 ? 's' : ''} remaining.`);
      } else if (data.job_id) {
        // Start in-place generation — grey out chapters, poll for progress
        setLangGenerating(true);
        setActiveLang(langSwitchTarget);
        setLangJobId(data.job_id);
        setActiveChapter(0);
        setCurrentTime(0);
      } else {
        captureMessage("Language switch returned unexpected response", "warning", {
          contentId,
          targetLang: langSwitchTarget,
          responseKeys: Object.keys(data || {}),
        });
        toast.error("Failed to switch language.");
      }
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error("No credits available. Please purchase more credits.");
      } else {
        captureError(err, { action: "switch_audio_language", contentId, targetLang: langSwitchTarget });
        toast.error("Failed to switch language. Please try again.");
      }
    } finally {
      setLangSwitching(false);
      setLangSwitchTarget(null);
    }
  };

  // Poll generation status for chapter-by-chapter streaming
  // Uses sequential setTimeout (not setInterval) to prevent overlapping requests
  // Handles both initial generation (jobId from URL) and language switch generation (langJobId)
  const activeJobId = jobId || langJobId;
  const isLangGeneration = !!langJobId && !jobId;

  useEffect(() => {
    if (!activeJobId) return;
    setIsStreaming(true);

    let pollCount = 0;
    const maxPolls = 250;
    let consecutiveErrors = 0;
    let stopped = false;

    const scheduleNext = () => {
      if (stopped) return;
      pollRef.current = setTimeout(poll, 2000);
    };

    const extractLangChapters = (marker) => {
      // For language generation, extract the translated chapters
      if (isLangGeneration && activeLang !== "en") {
        const trans = (marker.translations || {})[activeLang];
        if (trans && trans.chapters) {
          return { ...marker, chapters: trans.chapters, summary: trans.summary || marker.summary, active_lang: activeLang };
        }
      }
      return marker;
    };

    const poll = async () => {
      if (stopped) return;
      pollCount++;
      if (pollCount > maxPolls) {
        stopped = true;
        setIsStreaming(false);
        setLangGenerating(false);
        clearGenerationSearchParams();
        captureMessage("Audio generation polling timed out", "warning", {
          contentId,
          jobId: activeJobId,
          activeLang,
          pollCount,
        });
        toast.error("Generation timed out.");
        return;
      }
      try {
        const res = await contentApi.getGenerationStatus(activeJobId);
        const data = res.data;
        consecutiveErrors = 0;

        setGenProgress({ progress: data.progress, step: data.step });

        if (data.marker) {
          fullItemRef.current = data.marker;
          const displayItem = extractLangChapters(data.marker);
          setItem(displayItem);

          const displayChapters = displayItem.chapters || [];
          const readyCount = displayChapters.filter(ch => ch.chapter_audio_url).length;
          setChaptersReady(readyCount);
          setLoading(false);
        }

        if (data.status === "complete") {
          stopped = true;
          setIsStreaming(false);
          setGenProgress(null);
          clearGenerationSearchParams();
          if (data.marker) {
            fullItemRef.current = data.marker;
            const displayItem = extractLangChapters(data.marker);
            setItem(displayItem);
          }
          if (isLangGeneration) {
            setLangGenerating(false);
            setLangJobId(null);
            setPurchasedLangs(prev => [...new Set([...prev, activeLang])]);
            const remaining = Math.max(0, (user?.credits || 0) - 1);
            updateCredits(remaining);
            showCompletionToastOnce(
              activeJobId,
              `Audio guide ready in ${languages.find(l => l.code === activeLang)?.label || activeLang}! ${remaining} credit${remaining !== 1 ? 's' : ''} remaining.`
            );
          } else if (attachedJobId) {
            // Re-attached background generation (already paid for at purchase)
            setAttachedJobId(null);
            showCompletionToastOnce(activeJobId, "Audio guide ready!");
          } else {
            const remaining = Math.max(0, (user?.credits || 0) - 1);
            updateCredits(remaining);
            showCompletionToastOnce(
              activeJobId,
              `Audio guide ready! ${remaining} credit${remaining !== 1 ? 's' : ''} remaining.`
            );
          }
        } else if (data.status === "error") {
          stopped = true;
          setIsStreaming(false);
          setLangGenerating(false);
          setLangJobId(null);
          setAttachedJobId(null);
          setGenProgress(null);
          clearGenerationSearchParams();
          captureMessage("Audio generation job failed", "error", {
            contentId,
            jobId: activeJobId,
            activeLang,
            error: data.error,
          });
          toast.error(data.error || "Generation failed.");
        } else {
          scheduleNext();
        }
      } catch (err) {
        // 404 means job was already cleaned up (completed) — not a real error
        if (err?.response?.status === 404) {
          stopped = true;
          setIsStreaming(false);
          setLangGenerating(false);
          setLangJobId(null);
          setAttachedJobId(null);
          setGenProgress(null);
          clearGenerationSearchParams();
          return;
        }
        consecutiveErrors++;
        if (consecutiveErrors >= 5) {
          stopped = true;
          setIsStreaming(false);
          setLangGenerating(false);
          setLangJobId(null);
          setAttachedJobId(null);
          clearGenerationSearchParams();
          captureError(err, {
            action: "poll_generation_status",
            contentId,
            jobId: activeJobId,
            consecutiveErrors,
          });
          toast.error("Lost connection to server.");
        } else {
          scheduleNext();
        }
      }
    };

    poll(); // Immediate first poll

    return () => { stopped = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [activeJobId, attachedJobId, user, updateCredits, isLangGeneration, activeLang, languages, clearGenerationSearchParams, showCompletionToastOnce, contentId]);

  // Load content metadata + related (for non-streaming / cached content)
  useEffect(() => {
    if (jobId || langJobId) return; // Streaming/lang-gen mode handles its own data
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (
          incomingShareToken &&
          user &&
          !redeemedShareTokenRef.current.has(incomingShareToken)
        ) {
          redeemedShareTokenRef.current.add(incomingShareToken);
          try {
            const shareResponse = await userApi.redeemShare(incomingShareToken);
            setShareAccess(shareResponse.data?.status || "purchase_required");
          } catch (error) {
            redeemedShareTokenRef.current.delete(incomingShareToken);
            throw error;
          }
        }
        const [res, relRes] = await Promise.all([
          contentApi.getById(contentId, activeLang),
          contentApi.getRelated(contentId, 5).catch(() => ({ data: { items: [] } })),
        ]);
        if (cancelled) return;

        const data = res.data;
        fullItemRef.current = data;

        setItem(data);
        setRelated(relRes.data.items || []);

        // If this guide has no audio yet, check for an in-flight background
        // generation (Plan-a-Trip bundle purchase) and attach to its
        // progress so the user sees the chapters being prepared.
        const chs = data.chapters || [];
        const hasAnyAudio = chs.some(ch => ch.chapter_audio_url) || !!data.audio_url;
        if (!hasAnyAudio) {
          try {
            const g = await contentApi.getActiveGeneration(contentId, activeLang);
            if (!cancelled && g.data?.job_id) setAttachedJobId(g.data.job_id);
          } catch { /* no active job */ }
        }
      } catch (error) {
        captureError(error, { action: "load_audio_content", contentId, activeLang });
        toast.error("Failed to load content");
        navigate(-1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [contentId, navigate, activeLang, jobId, langJobId, incomingShareToken, user]);

  // Load related places once streaming completes
  useEffect(() => {
    if (!isStreaming && item && related.length === 0) {
      contentApi.getRelated(contentId, 5).then(r => setRelated(r.data.items || [])).catch((error) => {
        captureError(error, { action: "load_related_after_stream", contentId });
      });
    }
  }, [isStreaming, item, contentId, related.length]);

  // Detect legacy mode
  useEffect(() => {
    if (item) setLegacyMode(isLegacyAudio(item));
  }, [item]);

  // === LEGACY MODE: single audio file with chapter timing ===
  // Determine active chapter from current playback time (legacy mode only)
  useEffect(() => {
    if (!legacyMode || !item?.chapters?.length) return;
    const chapters = item.chapters;
    for (let i = chapters.length - 1; i >= 0; i--) {
      if (currentTime >= (chapters[i].audio_start_seconds || 0)) {
        if (i !== activeChapter) setActiveChapter(i);
        break;
      }
    }
  }, [currentTime, item, activeChapter, legacyMode]);

  // === PER-CHAPTER MODE: manage playlist ===
  const chapters = useMemo(() => item?.chapters || [], [item?.chapters]);
  const currentChapterAudioUrl = !legacyMode ? chapters[activeChapter]?.chapter_audio_url : null;

  // Set audio src for current chapter
  useEffect(() => {
    if (legacyMode || !audioRef.current || !currentChapterAudioUrl) return;
    if (audioRef.current.src !== currentChapterAudioUrl) {
      audioRef.current.src = currentChapterAudioUrl;
      audioRef.current.load();
    }
  }, [currentChapterAudioUrl, legacyMode]);

  // Auto-advance to next chapter when current ends (per-chapter mode)
  const handleChapterEnded = useCallback(() => {
    if (legacyMode) {
      setIsPlaying(false);
      return;
    }
    const nextIdx = activeChapter + 1;
    if (nextIdx < chapters.length && chapterHasAudio(chapters[nextIdx])) {
      setActiveChapter(nextIdx);
      setCurrentTime(0);
      // The useEffect above will change audio src, then we play
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
          setIsPlaying(true);
        }
      }, 100);
    } else {
      setIsPlaying(false);
    }
  }, [legacyMode, activeChapter, chapters]);

  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) {
      a.pause();
    } else {
      a.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const seekToChapter = useCallback((idx) => {
    const ch = chapters[idx];
    if (!ch || !audioRef.current) return;

    if (legacyMode) {
      audioRef.current.currentTime = ch.audio_start_seconds || 0;
      setCurrentTime(ch.audio_start_seconds || 0);
      setActiveChapter(idx);
    } else {
      if (!chapterHasAudio(ch)) return; // Can't seek to unready chapter
      setActiveChapter(idx);
      setCurrentTime(0);
      // src will be updated by useEffect, wait then play
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(() => {});
          setIsPlaying(true);
        }
      }, 150);
      return;
    }

    if (!isPlaying) {
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  }, [chapters, isPlaying, legacyMode]);

  const skipChapter = useCallback((dir) => {
    const next = activeChapter + dir;
    if (next >= 0 && next < chapters.length) {
      if (!legacyMode && !chapterHasAudio(chapters[next])) return;
      seekToChapter(next);
    }
  }, [activeChapter, chapters, seekToChapter, legacyMode]);

  const handleSeek = (e) => {
    const val = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = val;
      setCurrentTime(val);
    }
  };

  // Calculate total duration across all chapters (per-chapter mode)
  const totalDuration = legacyMode
    ? duration
    : chapters.reduce((sum, ch) => sum + (chapterDurations[ch.index ?? chapters.indexOf(ch)] || ch.duration_seconds || 0), 0);

  // Calculate total elapsed time across chapters (per-chapter mode)
  const totalElapsed = legacyMode
    ? currentTime
    : chapters.slice(0, activeChapter).reduce((sum, ch) => sum + (chapterDurations[ch.index ?? chapters.indexOf(ch)] || ch.duration_seconds || 0), 0) + currentTime;

  const shareText = `Look what I've been exploring in Culture Ken!`;
  const shareTitle = item ? item.name : "Culture Ken";
  const shareImage = resolveMediaUrl(item?.image_url) || null;
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const createShareUrl = async () => {
    const response = await userApi.trackShare(contentId, activeLang);
    const params = new URLSearchParams({ lang: activeLang });
    if (response.data?.share_token) params.set("share_token", response.data.share_token);
    return `${window.location.origin}/player/${contentId}?${params.toString()}`;
  };

  const handleShare = async () => {
    // Boolean lock — blocks ALL re-entry for 2 seconds
    if (shareLockRef.current) return;
    shareLockRef.current = true;
    setTimeout(() => { shareLockRef.current = false; }, 2000);

    if (navigator.share) {
      try {
        const shareUrl = await createShareUrl();
        const shareData = { title: shareTitle, text: `${shareText}\n\n${shareTitle}`, url: shareUrl };
        if (shareImage && navigator.canShare) {
          try {
            const resp = await fetch(shareImage);
            const blob = await resp.blob();
            const file = new File([blob], "place.jpg", { type: blob.type });
            const withFile = { ...shareData, files: [file] };
            if (navigator.canShare(withFile)) {
              await navigator.share(withFile);
              return;
            }
          } catch { /* fall through to share without image */ }
        }
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== "AbortError") toast.error("Sharing failed");
      }
    } else {
      setShowShareMenu(true);
    }
  };

  const shareToService = async (service) => {
    let shareUrl;
    try {
      shareUrl = await createShareUrl();
    } catch {
      toast.error("Could not create a share link");
      return;
    }
    const fullText = `${shareText}\n${shareTitle}\n${shareUrl}`;
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(`${shareText} - ${shareTitle}`);
    const urls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(fullText)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      x: `https://x.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(fullText)}`,
    };
    if (urls[service]) {
      window.open(urls[service], "_blank", "noopener,noreferrer");
    }
    setShowShareMenu(false);
  };

  const copyShareLink = async () => {
    try {
      const shareUrl = await createShareUrl();
      await navigator.clipboard.writeText(`${shareText}\n${shareTitle}\n${shareUrl}`);
      toast.success("Link copied!");
    } catch { toast.error("Failed to copy"); }
    setShowShareMenu(false);
  };

  const handlePrepareOwnedGuide = async () => {
    if (preparingGuide || !purchasedLangs.includes(activeLang)) return;
    setPreparingGuide(true);
    try {
      const { data } = await contentApi.generateSummary(contentId, activeLang, false);
      if (data.status === "complete" && data.marker) {
        fullItemRef.current = data.marker;
        setItem(data.marker);
      } else if (data.job_id) {
        setSearchParams((previous) => {
          const next = new URLSearchParams(previous);
          next.set("job_id", data.job_id);
          return next;
        }, { replace: true });
      } else {
        toast.error("Could not prepare this guide. Please try again.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not prepare this guide. Please try again.");
    } finally {
      setPreparingGuide(false);
    }
  };

  const handleSharedGuidePurchase = async () => {
    if (sharePurchasing) return;
    const isSubscriber = user?.subscription_status === "active";
    if (!isSubscriber && (user?.credits || 0) < 1) {
      toast.error("You need 1 credit to unlock this guide.");
      return;
    }

    setSharePurchasing(true);
    try {
      // The backend reuses cached audio, but still performs the normal atomic
      // entitlement and billing step for this recipient.
      const response = await contentApi.generateSummary(contentId, activeLang, true);
      const data = response.data;
      if (data.status === "complete" && data.marker) {
        fullItemRef.current = data.marker;
        setItem(data.marker);
        setPurchasedLangs((previous) => [...new Set([...previous, activeLang])]);
        setShareAccess("already_owned");
        await refreshUser?.();
        toast.success(isSubscriber ? "Guide added to your library." : "Guide unlocked for 1 credit.");
      } else if (data.job_id) {
        setShareAccess("already_owned");
        setSearchParams((previous) => {
          const next = new URLSearchParams(previous);
          next.set("job_id", data.job_id);
          return next;
        }, { replace: true });
        await refreshUser?.();
      } else {
        throw new Error("The guide could not be unlocked");
      }
    } catch (error) {
      captureError(error, { action: "purchase_shared_guide", contentId, activeLang });
      if (error?.response?.status === 402) {
        toast.error("You need 1 credit to unlock this guide.");
      } else {
        toast.error(error?.response?.data?.detail || "Could not unlock this guide.");
      }
    } finally {
      setSharePurchasing(false);
    }
  };

  // Legacy audio source
  const legacyAudioSrc = item?.audio_url && item.audio_url.startsWith("https://")
    ? item.audio_url
    : (item ? contentApi.getAudioStreamUrl(contentId, activeLang) : null);

  if (loading && !item && !isStreaming && !jobId && !langJobId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!item && !isStreaming) return null;

  // Show generation progress while waiting for first data
  if (!item && isStreaming) {
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="audio-player-page">
        <div className="fixed inset-0 paper-texture pointer-events-none" />
        <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost" size="icon"
            onClick={() => { if (pollRef.current) clearInterval(pollRef.current); navigate(-1); }}
            className="rounded-xl"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <span className="text-sm font-medium text-muted-foreground">Generating Audio Guide</span>
        </header>
        <main className="flex-1 flex flex-col items-center justify-center p-6 gap-6 relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Headphones className="w-8 h-8 text-primary animate-pulse" />
          </div>
          <div className="text-center space-y-2 max-w-xs">
            <h2 className="text-xl font-bold">{genProgress?.step || "Starting..."}</h2>
            <p className="text-sm text-muted-foreground" data-testid="patience-banner">
              This may take a minute! Thank you for your patience.
            </p>
          </div>
          {genProgress && (
            <div className="w-full max-w-xs space-y-1.5">
              <Progress value={genProgress.progress} className="h-2" />
              <p className="text-xs text-muted-foreground text-center tabular-nums">{genProgress.progress}%</p>
            </div>
          )}
        </main>
      </div>
    );
  }

  const hasAnyAudio = langGenerating
    ? false // Disable playback during language generation
    : sharedPlaybackLocked
    ? false
    : legacyMode
    ? !!item.audio_url
    : chapters.some(ch => ch.chapter_audio_url);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="audio-player-page">
      {/* Audio element */}
      <audio
        ref={audioRef}
        src={sharedPlaybackLocked ? undefined : (legacyMode ? legacyAudioSrc : currentChapterAudioUrl)}
        preload="auto"
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
        onLoadedMetadata={() => {
          applyAudioSettings();
          const dur = audioRef.current?.duration || 0;
          if (legacyMode) {
            setDuration(dur);
          } else {
            // Store actual duration for current chapter
            setChapterDurations(prev => ({ ...prev, [activeChapter]: dur }));
            setDuration(dur);
          }
        }}
        onEnded={handleChapterEnded}
        onPlay={() => { applyAudioSettings(); setIsPlaying(true); }}
        onPause={() => setIsPlaying(false)}
      />

      {/* Top Bar */}
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-3">
        <Button
          variant="ghost" size="icon"
          onClick={() => {
            if (pollRef.current) clearInterval(pollRef.current);
            if (item?.latitude && item?.longitude) {
              navigate(`/explore?lat=${item.latitude}&lng=${item.longitude}&zoom=19&marker=${contentId}`);
            } else {
              navigate(-1);
            }
          }}
          data-testid="player-back-btn"
        >
          <ChevronLeft className="w-6 h-6" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-bold text-base truncate text-foreground">{item.name}</h1>
          <p className="text-xs text-muted-foreground capitalize">{item.category || "Place"}</p>
        </div>
        <Button
          variant="ghost" size="icon"
          onClick={handleShare}
          disabled={isStreaming || sharedPlaybackLocked}
          className="rounded-xl w-11 h-11"
          data-testid="share-guide-btn"
        >
          <Share className="w-6 h-6" />
        </Button>
      </header>

      {/* Generation Progress Banner */}
      {isStreaming && genProgress && (
        <div className="px-4 py-3 bg-primary/5 border-b border-primary/20" data-testid="streaming-progress-banner">
          <div className="flex items-center gap-3">
            <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{genProgress.step}</p>
              <Progress value={genProgress.progress} className="h-1.5 mt-1.5" />
            </div>
            <span className="text-xs text-muted-foreground tabular-nums flex-shrink-0">{genProgress.progress}%</span>
          </div>
          {chaptersReady === 0 && (
            <p className="text-xs text-muted-foreground mt-2 ml-7 animate-in fade-in duration-500" data-testid="patience-banner">
              This may take a minute! Thank you for your patience.
            </p>
          )}
          {chaptersReady > 0 && (
            <p className="text-xs text-primary mt-1.5 ml-7">
              {chaptersReady} of {chapters.length} chapters ready — you can start listening!
            </p>
          )}
        </div>
      )}

      {/* Hero / Cover Area */}
      <div ref={heroRef} className="px-4 pt-6 pb-4 text-center scroll-mt-[60px]">
        <AudioGuideHero imageUrl={item.image_url} name={item.name} />
        <h2 className="text-lg font-bold text-foreground">{item.name}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {chapters.length} chapters
          {(item.audio_duration || totalDuration > 0) && ` · ${Math.round((item.audio_duration || totalDuration) / 60)} min`}
        </p>

        {/* User Star Rating — only shown if user has purchased this guide */}
        {purchasedLangs.length > 0 && (
        <div className="flex items-center justify-center gap-1 mt-2" data-testid="user-rating">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              onClick={() => handleRate(star)}
              onMouseEnter={() => setRatingHover(star)}
              onMouseLeave={() => setRatingHover(0)}
              className="p-0.5 transition-transform hover:scale-110 active:scale-95"
              data-testid={`rate-star-${star}`}
            >
              <Star
                className={`w-6 h-6 ${
                  star <= (ratingHover || userRating || 0)
                    ? "fill-amber-400 text-amber-400"
                    : "text-gray-300"
                }`}
              />
            </button>
          ))}
        </div>
        )}
        {purchasedLangs.length > 0 && (
        <p className="text-xs text-muted-foreground text-center mt-1">
          {userRating ? "Your rating" : "Rate this place"}
        </p>
        )}
        {/* Language selector */}
        {!sharedPlaybackLocked && (!isStreaming || langGenerating) && (
          <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
            <Button
              variant="outline" size="sm"
              className="gap-1.5 text-xs rounded-full"
              onClick={() => !langGenerating && setShowLangPicker(!showLangPicker)}
              disabled={langGenerating}
              data-testid="lang-toggle-btn"
            >
              <Globe className="w-3.5 h-3.5" />
              {languages.find(l => l.code === activeLang)?.label || activeLang.toUpperCase()}
              {langGenerating && <Loader2 className="w-3 h-3 animate-spin ml-1" />}
            </Button>
            {showLangPicker && (
              <div className="flex flex-wrap gap-1.5 justify-center" data-testid="lang-picker">
                {languages.map(l => (
                  <Button
                    key={l.code}
                    variant={l.code === activeLang ? "default" : "outline"}
                    size="sm"
                    className="text-xs rounded-full h-7 px-3"
                    onClick={() => {
                      if (l.code === activeLang) {
                        setShowLangPicker(false);
                        return;
                      }
                      setShowLangPicker(false);
                      if (hasLanguageAudio(l.code)) {
                        // Free switch — language already purchased
                        switchToExistingLanguage(l.code);
                      } else {
                        // Needs generation — show confirmation dialog
                        setLangSwitchTarget(l.code);
                      }
                    }}
                    data-testid={`lang-${l.code}`}
                  >
                    {l.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {shareAccess === "purchase_required" && (
        <div className="mx-4 mb-4 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="shared-guide-purchase">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Coins className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground">Unlock this shared guide</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Your friend shared the recommendation. Purchase access to add the existing audio guide to your library.
              </p>
            </div>
          </div>
          <Button
            className="mt-4 h-11 w-full rounded-xl"
            onClick={handleSharedGuidePurchase}
            disabled={sharePurchasing}
            data-testid="purchase-shared-guide-btn"
          >
            {sharePurchasing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Coins className="mr-2 h-4 w-4" />
            )}
            {user?.subscription_status === "active" ? "Add to library" : "Unlock for 1 credit"}
          </Button>
        </div>
      )}

      {/* Chapter List */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-44">
        {!hasAnyAudio && !isStreaming && !jobId && !langJobId && !attachedJobId &&
          !sharedPlaybackLocked && purchasedLangs.includes(activeLang) && (
          <div className="mb-4 space-y-2" data-testid="owned-guide-retry">
            <p className="text-sm text-muted-foreground">Your guide is not ready. Preparing it again will not use another credit.</p>
            <Button onClick={handlePrepareOwnedGuide} disabled={preparingGuide} data-testid="prepare-owned-guide-btn">
              {preparingGuide ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Headphones className="mr-2 h-4 w-4" />}
              Prepare audio guide
            </Button>
          </div>
        )}
        {/* Language generation banner */}
        {langGenerating && (
          <div className="mb-3 p-3 rounded-xl bg-primary/10 border border-primary/20" data-testid="lang-generating-banner">
            <div className="flex items-center gap-2 text-sm font-medium text-primary">
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating {languages.find(l => l.code === activeLang)?.label || activeLang} audio...
            </div>
            {genProgress && (
              <div className="mt-2">
                <Progress value={genProgress.progress} className="h-1.5" />
                <p className="text-xs text-muted-foreground mt-1">{genProgress.step}</p>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground">Chapters</span>
        </div>
        <div className="space-y-2">
          {chapters.map((ch, idx) => {
            const isReady = !sharedPlaybackLocked && (legacyMode || chapterHasAudio(ch));
            // During lang generation: treat all chapters without new-language audio as pending
            const isPending = !legacyMode && (!isReady || (langGenerating && !chapterHasAudio(ch)));
            const isCurrent = idx === activeChapter && isPlaying && !langGenerating;
            const isPast = idx < activeChapter && !langGenerating;
            const chDur = ch.duration_seconds
              ? formatTime(ch.duration_seconds)
              : `~${Math.ceil((ch.word_count || 100) / 135)} min`;

            return (
              <Card
                key={idx}
                className={`transition-all border ${
                  isPending
                    ? "border-border/30 opacity-40 cursor-default"
                    : isCurrent
                    ? "border-primary bg-primary/5 shadow-sm cursor-pointer"
                    : isPast
                    ? "border-border/50 opacity-70 cursor-pointer"
                    : "border-border hover:border-primary/40 cursor-pointer"
                }`}
                onClick={() => !isPending && seekToChapter(idx)}
                data-testid={`chapter-${idx}`}
              >
                <CardContent className="p-3 flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-bold ${
                      isPending
                        ? "bg-muted/50 text-muted-foreground/50"
                        : isCurrent
                        ? "bg-primary text-primary-foreground"
                        : isPast
                        ? "bg-muted text-muted-foreground"
                        : "bg-muted text-foreground"
                    }`}
                  >
                    {isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isCurrent ? (
                      <Volume2 className="w-4 h-4 animate-pulse" />
                    ) : (
                      idx + 1
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      isPending ? "text-muted-foreground/50" : isCurrent ? "text-primary" : "text-foreground"
                    }`}>
                      {ch.title}
                    </p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {isPending ? "Generating..." : `${ch.word_count} words`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                    {isPending ? (
                      <span className="text-muted-foreground/50">Pending</span>
                    ) : (
                      <>
                        <Clock className="w-3 h-3" />
                        <span>{chDur}</span>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Share Guide CTA */}
        {!isStreaming && shareAccess !== "purchase_required" && (
          <div className="mt-6 p-4 bg-primary/5 rounded-2xl border border-primary/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Share className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">Share this guide</p>
                <p className="text-xs text-muted-foreground mt-0.5">Let friends discover this place</p>
              </div>
              <Button
                size="sm" variant="outline"
                className="rounded-full gap-1.5 flex-shrink-0"
                onClick={handleShare}
                data-testid="share-guide-cta-btn"
              >
                <Share className="w-4 h-4" />
                Share
              </Button>
            </div>
          </div>
        )}

        {/* Share Menu */}
        {showShareMenu && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
          >
            <div
              className="bg-card w-full max-w-sm rounded-t-2xl sm:rounded-2xl p-5 space-y-4 shadow-xl"
              data-testid="share-menu"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold">Share</h3>
                <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setShowShareMenu(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Place preview */}
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl" data-testid="share-preview">
                <ReliablePlaceImage
                  imageUrl={shareImage}
                  name={shareTitle}
                  className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{shareTitle}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{shareText}</p>
                  <p className="text-xs text-primary mt-0.5 truncate">{shareUrl}</p>
                </div>
              </div>

              <div className={`grid gap-3 ${isMobile ? "grid-cols-4" : "grid-cols-3"}`}>
                {isMobile && (
                  <button onClick={() => shareToService("whatsapp")} className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-muted transition-colors" data-testid="share-whatsapp">
                    <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </div>
                    <span className="text-xs font-medium">WhatsApp</span>
                  </button>
                )}
                <button onClick={() => shareToService("facebook")} className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-muted transition-colors" data-testid="share-facebook">
                  <div className="w-12 h-12 rounded-full bg-[#1877F2] flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </div>
                  <span className="text-xs font-medium">Facebook</span>
                </button>
                <button onClick={() => shareToService("x")} className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-muted transition-colors" data-testid="share-x">
                  <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </div>
                  <span className="text-xs font-medium">X</span>
                </button>
                {!isMobile && (
                  <button onClick={() => shareToService("email")} className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-muted transition-colors" data-testid="share-email">
                    <div className="w-12 h-12 rounded-full bg-muted-foreground/30 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    </div>
                    <span className="text-xs font-medium">Email</span>
                  </button>
                )}
                <button onClick={copyShareLink} className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-muted transition-colors" data-testid="share-copy-link">
                  <div className="w-12 h-12 rounded-full bg-muted-foreground/20 flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-foreground" />
                  </div>
                  <span className="text-xs font-medium">Copy Link</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Related Places */}
        {related.length > 0 && (
          <div className="mt-8" data-testid="related-places-section">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">Explore Nearby</span>
            </div>
            <div className="space-y-2">
              {related.map((place) => (
                <Card
                  key={place.id}
                  className="cursor-pointer border border-border hover:border-primary/40 transition-all"
                  onClick={() => navigate(`/explore?lat=${place.latitude}&lng=${place.longitude}&zoom=17&marker=${place.id}`)}
                  data-testid={`related-${place.id}`}
                >
                  <CardContent className="p-3 flex items-center gap-3">
                    <ReliablePlaceImage
                      imageUrl={place.image_url}
                      name={place.name}
                      className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                      iconClassName="w-5 h-5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate text-foreground">{place.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground capitalize">{place.category}</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-muted-foreground">{place.distance_km} km</span>
                        {place.has_audio && (
                          <>
                            <span className="text-xs text-muted-foreground">·</span>
                            <Headphones className="w-3 h-3 text-primary" />
                          </>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Fixed Player Controls */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-t border-border pb-safe">
        <div className="px-4 pt-3">
          {/* Progress bar */}
          <input
            type="range"
            min={0}
            max={legacyMode ? (duration || 100) : (totalDuration || 100)}
            value={legacyMode ? currentTime : totalElapsed}
            onChange={(e) => {
              if (legacyMode) {
                handleSeek(e);
              } else {
                // For per-chapter mode, seeking in the global bar
                let target = parseFloat(e.target.value);
                let cumulative = 0;
                for (let i = 0; i < chapters.length; i++) {
                  const chDur = chapterDurations[i] || chapters[i].duration_seconds || 0;
                  if (cumulative + chDur > target) {
                    if (chapterHasAudio(chapters[i])) {
                      if (i !== activeChapter) setActiveChapter(i);
                      setTimeout(() => {
                        if (audioRef.current) {
                          audioRef.current.currentTime = target - cumulative;
                          setCurrentTime(target - cumulative);
                        }
                      }, i !== activeChapter ? 200 : 0);
                    }
                    break;
                  }
                  cumulative += chDur;
                }
              }
            }}
            className="audio-progress w-full h-1.5 mb-1"
            style={{ "--progress": `${totalDuration ? ((legacyMode ? currentTime : totalElapsed) / (legacyMode ? duration : totalDuration)) * 100 : 0}%` }}
            data-testid="player-progress"
          />
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>{formatTime(legacyMode ? currentTime : totalElapsed)}</span>
            <span className="text-foreground/70 font-medium text-xs">
              {chapters[activeChapter]?.title || ""}
            </span>
            <span>{formatTime(legacyMode ? duration : totalDuration)}</span>
          </div>
        </div>

        {/* Transport controls */}
        <div className="flex items-center justify-center gap-4 pb-4">
          <div className="relative w-12">
            <Button
              variant="ghost" size="sm"
              className="rounded-full font-mono text-xs w-12 h-8"
              onClick={toggleSpeedSlider}
              data-testid="speed-btn"
            >
              {speed}x
            </Button>
            {showSpeedSlider && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowSpeedSlider(false)} onTouchStart={() => setShowSpeedSlider(false)} />
                <div
                  className="absolute bottom-full left-0 mb-3 z-50 bg-card border border-border rounded-2xl shadow-xl p-3 w-48"
                  data-testid="speed-slider-popup"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground">Speed</span>
                    <span className="text-sm font-bold text-foreground">{speed}x</span>
                  </div>
                  <input
                    type="range"
                    min={0.75}
                    max={2}
                    step={0.25}
                    value={speed}
                    onChange={(e) => setSpeed(parseFloat(e.target.value))}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer accent-primary bg-muted"
                    data-testid="speed-slider"
                  />
                  <div className="flex justify-between mt-1.5">
                    <span className="text-[10px] text-muted-foreground">0.75x</span>
                    <span className="text-[10px] text-muted-foreground">2x</span>
                  </div>
                </div>
              </>
            )}
          </div>

          <Button
            variant="ghost" size="icon"
            onClick={() => skipChapter(-1)}
            disabled={activeChapter <= 0}
            data-testid="player-prev-btn"
          >
            <SkipBack className="w-6 h-6" />
          </Button>

          <Button
            size="icon"
            className="w-16 h-16 rounded-full shadow-lg"
            onClick={togglePlay}
            disabled={!hasAnyAudio}
            data-testid="player-play-btn"
          >
            {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-0.5" />}
          </Button>

          <Button
            variant="ghost" size="icon"
            onClick={() => skipChapter(1)}
            disabled={activeChapter >= chapters.length - 1 || (!legacyMode && !chapterHasAudio(chapters[activeChapter + 1]))}
            data-testid="player-next-btn"
          >
            <SkipForward className="w-6 h-6" />
          </Button>

          <div className="relative">
            <Button
              variant="ghost" size="sm"
              className="rounded-full w-12 h-8"
              onClick={() => {
                if (audioCtxRef.current?.state === "suspended") {
                  audioCtxRef.current.resume();
                }
                setSpeakerMode(prev => {
                  const next = !prev;
                  if (gainNodeRef.current) {
                    gainNodeRef.current.gain.value = next ? 1.0 : 0.15;
                  }
                  setModeToast(next ? "Loudspeaker" : "Earpiece");
                  setTimeout(() => setModeToast(null), 1500);
                  return next;
                });
              }}
              title={speakerMode ? "Switch to earpiece" : "Switch to speaker"}
              data-testid="speaker-toggle-btn"
            >
              {speakerMode ? <Volume2 className="w-4 h-4" /> : <Volume1 className="w-4 h-4" />}
            </Button>
            {modeToast && (
              <div className="absolute bottom-full right-0 mb-2 whitespace-nowrap bg-foreground text-background text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg pointer-events-none z-50">
                {modeToast}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Language switch confirmation dialog */}
      <Dialog open={!!langSwitchTarget} onOpenChange={(open) => { if (!open) setLangSwitchTarget(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Switch Language</DialogTitle>
            <DialogDescription>
              Switching to {languages.find(l => l.code === langSwitchTarget)?.label || langSwitchTarget} will
              re-generate the audio summary and use 1 additional credit. Continue?
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1 h-11 rounded-xl"
              onClick={() => setLangSwitchTarget(null)}
              data-testid="lang-switch-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="flex-1 h-11 rounded-xl"
              onClick={handleLanguageSwitch}
              disabled={langSwitching}
              data-testid="lang-switch-confirm-btn"
            >
              {langSwitching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
